import { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { authAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { TrendingUp, Lock, Eye, EyeOff, CheckCircle, AlertCircle, Shield, Key } from 'lucide-react';
import { toast } from 'sonner';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(true);
  const [validToken, setValidToken] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [success, setSuccess] = useState(false);
  
  const [masterPassword, setMasterPassword] = useState('');
  const [investorPassword, setInvestorPassword] = useState('');
  const [confirmMaster, setConfirmMaster] = useState('');
  const [confirmInvestor, setConfirmInvestor] = useState('');
  const [showMaster, setShowMaster] = useState(false);
  const [showInvestor, setShowInvestor] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }

    // Verify token
    console.log('Verifying reset token:', token);
    authAPI.verifyResetToken(token)
      .then((res) => {
        console.log('Token verification success:', res.data);
        setValidToken(true);
        setUserInfo(res.data);
      })
      .catch((err) => {
        console.error('Token verification failed:', err.response?.data || err.message);
        setValidToken(false);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (masterPassword.length < 6) {
      toast.error('Master password must be at least 6 characters');
      return;
    }
    if (investorPassword.length < 6) {
      toast.error('Investor password must be at least 6 characters');
      return;
    }
    if (masterPassword !== confirmMaster) {
      toast.error('Master passwords do not match');
      return;
    }
    if (investorPassword !== confirmInvestor) {
      toast.error('Investor passwords do not match');
      return;
    }
    if (masterPassword === investorPassword) {
      toast.error('Master and Investor passwords must be different');
      return;
    }

    setSubmitting(true);
    try {
      await authAPI.resetPassword(token, masterPassword, investorPassword);
      setSuccess(true);
      toast.success('Password reset successful!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!token || !validToken) {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center p-4">
        <div className="w-full max-w-md text-center">
          <div className="glass-card p-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-500/10 mb-4">
              <AlertCircle className="w-8 h-8 text-red-500" />
            </div>
            <h2 className="text-xl font-semibold text-white mb-2">Invalid or Expired Link</h2>
            <p className="text-zinc-400 text-sm mb-6">
              This password reset link is invalid or has expired. Please request a new one.
            </p>
            <Link to="/forgot-password">
              <Button className="bg-primary hover:bg-primary/90">Request New Link</Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-[#09090B] flex items-center justify-center p-4">
        <div className="w-full max-w-md text-center">
          <div className="glass-card p-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-500/10 mb-4">
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
            <h2 className="text-xl font-semibold text-white mb-2">Password Reset Successful!</h2>
            <p className="text-zinc-400 text-sm mb-6">
              Your Master and Investor passwords have been updated. You can now login with your new credentials.
            </p>
            <Button 
              className="bg-primary hover:bg-primary/90"
              onClick={() => navigate('/login')}
            >
              Go to Login
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090B] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-primary/10 mb-4">
            <TrendingUp className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-2xl font-bold text-white font-['Outfit']">GeneiProFx</h1>
          <p className="text-zinc-500 text-sm mt-1">Set Your New Passwords</p>
        </div>

        {/* Card */}
        <div className="glass-card p-6">
          <div className="text-center mb-6">
            <h2 className="text-xl font-semibold text-white mb-2">
              Welcome back, {userInfo?.first_name || 'Trader'}!
            </h2>
            <p className="text-zinc-400 text-sm">
              Set your new Master and Investor passwords below.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Master Password Section */}
            <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
              <div className="flex items-center gap-2 mb-3">
                <Key className="w-4 h-4 text-blue-400" />
                <span className="text-blue-400 font-medium text-sm">Master Password (For Trading)</span>
              </div>
              
              <div className="space-y-3">
                <div className="space-y-1">
                  <Label htmlFor="master" className="text-zinc-300 text-xs">New Master Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <Input
                      id="master"
                      type={showMaster ? 'text' : 'password'}
                      value={masterPassword}
                      onChange={(e) => setMasterPassword(e.target.value)}
                      placeholder="Enter master password"
                      className="pl-10 pr-10 bg-zinc-900/50 border-zinc-800 text-white"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowMaster(!showMaster)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
                    >
                      {showMaster ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                
                <div className="space-y-1">
                  <Label htmlFor="confirmMaster" className="text-zinc-300 text-xs">Confirm Master Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <Input
                      id="confirmMaster"
                      type={showMaster ? 'text' : 'password'}
                      value={confirmMaster}
                      onChange={(e) => setConfirmMaster(e.target.value)}
                      placeholder="Confirm master password"
                      className="pl-10 bg-zinc-900/50 border-zinc-800 text-white"
                      required
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Investor Password Section */}
            <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-lg">
              <div className="flex items-center gap-2 mb-3">
                <Shield className="w-4 h-4 text-amber-400" />
                <span className="text-amber-400 font-medium text-sm">Investor Password (Read-Only)</span>
              </div>
              
              <div className="space-y-3">
                <div className="space-y-1">
                  <Label htmlFor="investor" className="text-zinc-300 text-xs">New Investor Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <Input
                      id="investor"
                      type={showInvestor ? 'text' : 'password'}
                      value={investorPassword}
                      onChange={(e) => setInvestorPassword(e.target.value)}
                      placeholder="Enter investor password"
                      className="pl-10 pr-10 bg-zinc-900/50 border-zinc-800 text-white"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowInvestor(!showInvestor)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white"
                    >
                      {showInvestor ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                
                <div className="space-y-1">
                  <Label htmlFor="confirmInvestor" className="text-zinc-300 text-xs">Confirm Investor Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <Input
                      id="confirmInvestor"
                      type={showInvestor ? 'text' : 'password'}
                      value={confirmInvestor}
                      onChange={(e) => setConfirmInvestor(e.target.value)}
                      placeholder="Confirm investor password"
                      className="pl-10 bg-zinc-900/50 border-zinc-800 text-white"
                      required
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Info Box */}
            <div className="p-3 bg-zinc-900/50 rounded-lg border border-zinc-800">
              <p className="text-zinc-400 text-xs">
                <strong className="text-zinc-300">Remember:</strong>
                <br />• <span className="text-blue-400">Master Password</span> - Full trading access
                <br />• <span className="text-amber-400">Investor Password</span> - Read-only access (cannot trade)
                <br />• Passwords must be at least 6 characters
                <br />• Passwords must be different from each other
              </p>
            </div>

            <Button
              type="submit"
              className="w-full bg-primary hover:bg-primary/90"
              disabled={submitting}
            >
              {submitting ? 'Resetting...' : 'Reset Passwords'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
